import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Box,
  Text,
  Button,
  useMedia,
  Layout,
  VerticalSpacing,
} from '../../components/Boneyard';
import Link from '@g4/next-furniture/Link';
import tracking from '@g4/g4-analytics';
import { withValue, hook } from '@g4/automation-hooks';
import { kebabCase } from 'lodash';

import { useSSRQuery } from '../../graphql';
import { greyA100, grey100 } from '../../theme/theme/colors';
import { HOW_TO_ALLEGIANT } from '../../../automationHooks.json';
import HOW_TO_ALLEGIANT_QUERY from './howToAllegiant/howToAllegiant.query.graphql.js';

const TextBox = styled(Box)`
  text-align: center;
  font-weight: 500;

  ${props => props.theme.query.md} {
    text-align: left;
  }
`;

const SectionText = styled.div`
  font-size: 14px;
`;

const ItemBox = styled(Box)`
  text-align: center;
  padding: 0 9px;
  border-right: 1px solid ${grey100};

  &:last-child {
    border-right: none;
    padding-right: 0;
  }

  &:first-child {
    padding-left: 0;
  }

  ${props => props.theme.query.md} {
    border-right: none;
  }
`;

const Icon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  margin-bottom: ${props => props.theme.spacing(2)};

  ${props => props.theme.query.lg} {
    width: 48px;
    height: 48px;
    margin-bottom: ${props => props.theme.spacing(7)};
  }
`;

const HowToAllegiant = () => {
  const mq = useMedia();
  const isMobile = mq.lte('sm');
  const { loading, error, data } = useSSRQuery(HOW_TO_ALLEGIANT_QUERY);

  if (loading) {
    return null;
  }

  if (error) {
    throw error;
  }

  const sectionData = data.application.howToAllegiant;
  const linkUrl =
    window.location.href.slice(0, -1) + sectionData.clickThrough.url;

  const trackLearnMoreClick = () => {
    tracking.trackEvent('site_navigation_clicks', linkUrl, 'Learn More');
  };

  const SectionButton = () => (
    <Link
      href={sectionData.clickThrough.url}
      target={
        sectionData.clickThrough.target
          ? `_${sectionData.clickThrough.target.toLowerCase()}`
          : '_blank'
      }
    >
      {({ anchorProps }) => (
        <Button
          automationHook={withValue(HOW_TO_ALLEGIANT.hook, 'button')}
          type="secondary"
          fullWidth
          as="a"
          {...anchorProps}
          onClick={trackLearnMoreClick}
        >
          {sectionData.clickThrough.title
            ? sectionData.clickThrough.title
            : 'Learn More'}
        </Button>
      )}
    </Link>
  );

  return (
    <React.Fragment>
      <Layout.FullWidth>
        <Layout.Strip color={greyA100}>
          <Box
            d="flex"
            direction={isMobile ? 'column' : 'row'}
            automationHook={HOW_TO_ALLEGIANT.hook}
            pt={6}
            pb={6}
            alignItems="center"
            w={'100%'}
          >
            <TextBox w={isMobile ? '240px' : '200px'} mr={isMobile ? 0 : 16}>
              {sectionData.title && (
                <Text
                  size={22}
                  automationHook={withValue(HOW_TO_ALLEGIANT.hook, 'title')}
                >
                  {sectionData.title}
                </Text>
              )}
              {sectionData.description && (
                <Box mt={isMobile ? 3 : 4} mb={5}>
                  <SectionText
                    {...hook(HOW_TO_ALLEGIANT.hook, 'description')}
                    dangerouslySetInnerHTML={{
                      __html: sectionData.description,
                    }}
                  ></SectionText>
                </Box>
              )}
              {!isMobile && <SectionButton />}
            </TextBox>
            <Box d="flex" justifyContent="center" flex={'1 1'} w={'100%'}>
              {sectionData.items.map((item, index) => {
                return (
                  <ItemBox
                    d="flex"
                    flex={'1 1'}
                    direction="column"
                    alignItems="center"
                    key={'hta-item-' + index}
                  >
                    <Icon
                      src={item.image}
                      alt=""
                      {...hook(
                        withValue(
                          HOW_TO_ALLEGIANT.hook,
                          'item_' + kebabCase(item.title) + '_icon'
                        )
                      )}
                    />
                    <Text
                      automationHook={withValue(
                        HOW_TO_ALLEGIANT.hook,
                        'item_' + kebabCase(item.title) + '_title'
                      )}
                      size={isMobile ? 12 : 14}
                      weight={500}
                    >
                      {item.title}
                    </Text>
                  </ItemBox>
                );
              })}
            </Box>
            {isMobile && (
              <Box mt={6} w={'240px'}>
                <SectionButton />
              </Box>
            )}
          </Box>
        </Layout.Strip>
      </Layout.FullWidth>
      <VerticalSpacing size={5} />
    </React.Fragment>
  );
};

HowToAllegiant.propTypes = {
  image: PropTypes.string,
};

export default HowToAllegiant;
